import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Briefcase, ChevronDown, RotateCcw, Save, Loader2, TrendingUp } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { formatCurrency, API, authConfig } from "./constants";

// IRD reimbursement rates (Tier 1 + Tier 2 for the 2025/26 income year)
// Source: https://www.ird.govt.nz/income-tax/income-tax-for-businesses-and-organisations/types-of-business-expenses/vehicle-expenses
const IRD_RATES = {
  petrol_diesel: { label: "Petrol / Diesel", tier1: 1.04, tier2: 0.35 },
  petrol_hybrid: { label: "Petrol Hybrid",   tier1: 0.95, tier2: 0.21 },
  electric:      { label: "Electric",        tier1: 0.83, tier2: 0.12 },
};
const TIER1_CAP_KM = 14000;

// Map fuel selection → IRD bracket
function inferBracket(fuelType, vehicleType) {
  if (fuelType === "electric") return "electric";
  if (fuelType === "petrol_hybrid" || vehicleType === "hybrid") return "petrol_hybrid";
  return "petrol_diesel";
}

const LABEL_SUGGESTIONS = ["Client Visit", "Site Visit", "Office", "Conference", "Training", "Delivery"];

export default function MileageReimbursement({ liveResult, selectedFuelType, selectedVehicle, returnTrip, user, originInput, destinationInput, tripName, onSaved }) {
  const distance = liveResult?.distance_km || 0;
  const tripCost = liveResult?.total_cost || 0;

  const defaultBracket = useMemo(
    () => inferBracket(selectedFuelType, selectedVehicle?.vehicle_type),
    [selectedFuelType, selectedVehicle]
  );

  const [bracket, setBracket] = useState(defaultBracket);
  const [override, setOverride] = useState(() => localStorage.getItem("mileage_override_rate") || "");
  const [label, setLabel] = useState(() => localStorage.getItem("mileage_last_label") || "");
  const [showBracketDD, setShowBracketDD] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fyData, setFyData] = useState(null);

  // Fetch current NZ FY km usage for Tier 1/2 split
  const fetchFy = useCallback(async () => {
    if (!user) { setFyData(null); return; }
    try {
      const resp = await axios.get(`${API}/mileage-trips/fy-summary`, authConfig());
      setFyData(resp.data);
    } catch (e) {
      setFyData(null);
    }
  }, [user]);

  useEffect(() => { fetchFy(); }, [fetchFy]);

  // Persist override
  useEffect(() => {
    if (override) localStorage.setItem("mileage_override_rate", override);
    else localStorage.removeItem("mileage_override_rate");
  }, [override]);

  // Re-sync bracket if fuel changes (only when user hasn't manually chosen)
  useEffect(() => {
    setBracket(defaultBracket);
  }, [defaultBracket]);

  const rates = IRD_RATES[bracket];
  const fyUsed = fyData?.total_km || 0;
  const overrideNum = override && parseFloat(override) > 0 ? parseFloat(override) : null;

  // Compute IRD-compliant blended rate based on current FY km + this trip's distance
  // Split: any km up to TIER1_CAP_KM at tier1; any km beyond at tier2
  const tierSplit = useMemo(() => {
    if (distance <= 0) return { tier1Km: 0, tier2Km: 0, blendedRate: rates.tier1 };
    const remainingTier1 = Math.max(0, TIER1_CAP_KM - fyUsed);
    const tier1Km = Math.min(distance, remainingTier1);
    const tier2Km = Math.max(0, distance - tier1Km);
    const blendedReimb = tier1Km * rates.tier1 + tier2Km * rates.tier2;
    const blendedRate = distance > 0 ? blendedReimb / distance : rates.tier1;
    return { tier1Km, tier2Km, blendedRate };
  }, [distance, fyUsed, rates]);

  const usedRate = overrideNum !== null ? overrideNum : tierSplit.blendedRate;
  const reimbursement = distance * usedRate;
  const net = reimbursement - tripCost;
  const showTierSplit = !overrideNum && tierSplit.tier2Km > 0;

  const handleSave = async () => {
    if (!user) { toast.error("Sign in to save work trips"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/mileage-trips`, {
        trip_name: tripName || "",
        label: label || "",
        distance_km: distance,
        fuel_cost: tripCost,
        reimbursement_rate: usedRate,
        reimbursement_total: reimbursement,
        net,
        bracket,
        is_override: !!(override && parseFloat(override) > 0),
        origin: originInput || "",
        destination: destinationInput || "",
      }, authConfig());
      if (label) localStorage.setItem("mileage_last_label", label);
      toast.success("Work trip saved");
      // refresh FY summary so the next trip uses up-to-date tier budget
      fetchFy();
      if (onSaved) onSaved();
    } catch (e) {
      toast.error("Could not save trip");
    } finally {
      setSaving(false);
    }
  };

  if (!liveResult || distance <= 0) return null;

  return (
    <div className="sf-card" data-testid="mileage-reimbursement-panel" style={{ marginBottom: 0, borderRadius: 20, padding: 18 }}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between"
        data-testid="mileage-reimbursement-toggle"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-[#E52020]/10 border border-[#E52020]/20 flex items-center justify-center shrink-0">
            <Briefcase className="w-4 h-4 text-[#E52020]" />
          </div>
          <div className="text-left min-w-0">
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#999999]">Work Trip Calculator</div>
            <div className="text-base font-bold text-[#1A1A1A] font-mono truncate" data-testid="mileage-reimbursement-amount">
              {formatCurrency(reimbursement)}
              <span className={`ml-2 text-xs font-normal ${net >= 0 ? "text-[#16A34A]" : "text-[#E52020]"}`} data-testid="mileage-reimbursement-net">
                {net >= 0 ? `+${formatCurrency(net)}` : `${formatCurrency(net)}`}
              </span>
            </div>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-[#666666] transition-transform shrink-0 ${expanded ? "rotate-180" : ""}`} />
      </button>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-[#EBE6DC] space-y-3">
          {/* Label */}
          <div>
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#999999] mb-1.5">Label (purpose)</div>
            <input
              type="text"
              placeholder="e.g. Client visit, Site inspection…"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={48}
              className="w-full bg-[#FAFAF7] border border-[#EBE6DC] rounded-xl px-3 py-2.5 text-sm text-[#1A1A1A] outline-none focus:border-[#E52020] focus:bg-white transition-colors"
              data-testid="mileage-label-input"
            />
            <div className="flex gap-1.5 mt-2 flex-wrap" data-testid="mileage-label-chips">
              {LABEL_SUGGESTIONS.map(s => (
                <button
                  key={s}
                  onClick={() => setLabel(s)}
                  className={`text-[10px] font-semibold tracking-[0.04em] px-2.5 py-1 rounded-full border transition-all ${label === s ? "bg-[#E52020] border-[#E52020] text-white" : "bg-transparent border-[#EBE6DC] text-[#666666] hover:border-[#E52020] hover:text-[#E52020]"}`}
                  data-testid={`mileage-label-chip-${s.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>


          {/* Vehicle bracket */}
          <div>
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#999999] mb-1.5">Vehicle category (IRD)</div>
            <div className="relative">
              <button
                onClick={() => setShowBracketDD(!showBracketDD)}
                className="w-full flex items-center justify-between bg-[#FAFAF7] border border-[#EBE6DC] rounded-xl px-3 py-2.5 text-sm text-[#1A1A1A] hover:border-[#E52020] transition-colors"
                data-testid="mileage-bracket-btn"
              >
                <span>{rates.label} — T1 ${rates.tier1}/km · T2 ${rates.tier2}/km</span>
                <ChevronDown className={`w-4 h-4 text-[#666666] transition-transform ${showBracketDD ? "rotate-180" : ""}`} />
              </button>
              {showBracketDD && (
                <div className="sf-dropdown" data-testid="mileage-bracket-dropdown">
                  {Object.entries(IRD_RATES).map(([key, b]) => (
                    <button
                      key={key}
                      onClick={() => { setBracket(key); setShowBracketDD(false); }}
                      className={`sf-dropdown-item ${bracket === key ? "active" : ""}`}
                      data-testid={`mileage-bracket-${key}`}
                    >
                      <span>{b.label}</span>
                      <span className="font-mono text-xs">T1 ${b.tier1} · T2 ${b.tier2}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* FY usage banner — only shown to signed-in users with data */}
          {fyData && (
            <div
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-xs ${
                fyData.in_tier2
                  ? "bg-amber-50 border border-amber-200 text-amber-900"
                  : "bg-[#16A34A]/8 border border-[#16A34A]/20 text-[#1A1A1A]"
              }`}
              data-testid="mileage-fy-banner"
            >
              <TrendingUp className="w-4 h-4 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold">
                  {fyData.fy_label}: {fyData.total_km.toLocaleString()} km logged
                </div>
                <div className="text-[10px] text-[#666666] mt-0.5">
                  {fyData.in_tier2
                    ? `Tier 1 budget used. Next ${distance.toFixed(0)} km calculated at Tier 2 ($${rates.tier2}/km).`
                    : `${fyData.tier1_remaining_km.toLocaleString()} km remaining at Tier 1 ($${rates.tier1}/km).`}
                </div>
              </div>
            </div>
          )}

          {/* Override */}
          <div>
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#999999] mb-1.5">Custom rate (overrides IRD)</div>
            <div className="flex items-center gap-2">
              <div className="flex-1 flex items-center bg-[#FAFAF7] border border-[#EBE6DC] rounded-xl px-3 py-2 focus-within:border-[#E52020]">
                <span className="text-[#E52020] font-mono font-bold mr-1">$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  placeholder={tierSplit.blendedRate.toFixed(2)}
                  value={override}
                  onChange={(e) => setOverride(e.target.value)}
                  className="flex-1 bg-transparent border-none outline-none text-sm font-mono text-[#1A1A1A]"
                  min="0"
                  step="0.01"
                  data-testid="mileage-override-input"
                />
                <span className="text-xs font-mono text-[#999999]">/km</span>
              </div>
              {override && (
                <button
                  onClick={() => setOverride("")}
                  className="text-xs font-bold tracking-[0.1em] uppercase text-[#666666] hover:text-[#E52020] border border-[#EBE6DC] rounded-full px-3 py-2 transition-colors"
                  data-testid="mileage-override-reset"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Breakdown */}
          <div className="bg-[#FAFAF7] rounded-xl p-3 space-y-1.5 text-xs font-mono" data-testid="mileage-breakdown">
            {showTierSplit ? (
              <>
                <div className="flex justify-between text-[#666666]" data-testid="mileage-breakdown-tier1">
                  <span>{tierSplit.tier1Km.toFixed(1)} km @ ${rates.tier1.toFixed(2)} (Tier 1)</span>
                  <span className="text-[#1A1A1A]">{formatCurrency(tierSplit.tier1Km * rates.tier1)}</span>
                </div>
                <div className="flex justify-between text-[#666666]" data-testid="mileage-breakdown-tier2">
                  <span>{tierSplit.tier2Km.toFixed(1)} km @ ${rates.tier2.toFixed(2)} (Tier 2)</span>
                  <span className="text-[#1A1A1A]">{formatCurrency(tierSplit.tier2Km * rates.tier2)}</span>
                </div>
                <div className="flex justify-between text-[#666666] pt-1.5 border-t border-[#EBE6DC]">
                  <span>Reimbursement{returnTrip ? " (return)" : ""}</span>
                  <span className="text-[#1A1A1A] font-bold">{formatCurrency(reimbursement)}</span>
                </div>
              </>
            ) : (
              <div className="flex justify-between text-[#666666]">
                <span>{distance} km{returnTrip ? " (return)" : ""} × ${usedRate.toFixed(2)}/km</span>
                <span className="text-[#1A1A1A] font-bold">{formatCurrency(reimbursement)}</span>
              </div>
            )}
            <div className="flex justify-between text-[#666666]">
              <span>Trip cost</span>
              <span>− {formatCurrency(tripCost)}</span>
            </div>
            <div className="flex justify-between pt-1.5 border-t border-[#EBE6DC] font-bold">
              <span className="text-[#1A1A1A]">Net</span>
              <span className={net >= 0 ? "text-[#16A34A]" : "text-[#E52020]"} data-testid="mileage-net-detail">
                {net >= 0 ? "+" : ""}{formatCurrency(net)}
              </span>
            </div>
          </div>

          <p className="text-[10px] text-[#999999] leading-relaxed">
            IRD Tier 1 rates apply to the first 14,000 km per income year. Tier 2 (lower) rates apply after.
            Override anytime to match your employer's reimbursement policy.
          </p>

          {/* Save button — only for logged-in users */}
          {user ? (
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full mt-2 flex items-center justify-center gap-2 py-3 rounded-full bg-[#1A1A1A] text-white text-xs font-bold tracking-[0.14em] uppercase hover:bg-[#E52020] transition-all disabled:opacity-50"
              data-testid="save-mileage-trip-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? "Saving..." : "Save Work Trip"}
            </button>
          ) : (
            <p className="text-[10px] text-center text-[#999999] italic" data-testid="signin-prompt-mileage">
              Sign in to save work trips for IRD claims.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
